import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Users, Plus, Search, Trash2, Pencil, RefreshCw, Loader2, Copy, FlaskConical, Share2, ShieldCheck, Download, ClipboardCopy,
  Ban, CheckCircle2, XCircle, ExternalLink, AppWindow, Wifi, WifiOff, Tv, MapPin, Clock, Smartphone, Server, Eye, EyeOff, Wrench, AlertTriangle,
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import api, { formatApiError } from "@/lib/api";
import { copyToClipboard } from "@/lib/clipboard";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const emptyForm = {
  full_name: "", username: "", password: "", mac_address: "", device_key: "",
  plan_id: "", panel_id: "", template_id: "", application_id: "", is_trial: false, notes: "", expiry_date: "",
};

function StatusPill({ status }) {
  const map = {
    active: "bg-success/15 text-success border-success/30",
    expired: "bg-destructive/15 text-destructive border-destructive/30",
    disabled: "bg-muted text-muted-foreground border-border",
    inactive: "bg-secondary text-muted-foreground border-border",
  };
  return <Badge variant="outline" className={`capitalize ${map[status] || map.inactive}`}>{status}</Badge>;
}

function RemainingDays({ customer }) {
  if (customer.status === "disabled") return <span className="text-xs text-muted-foreground">—</span>;
  if (!customer.expiry_date) return <span className="text-xs text-muted-foreground">—</span>;
  const days = Math.ceil((new Date(customer.expiry_date) - new Date()) / 86400000);
  if (days <= 0) return <Badge variant="outline" className="bg-destructive/15 text-destructive border-destructive/30" data-testid="remaining-badge">Expired</Badge>;
  const cls = days > 7
    ? "bg-success/15 text-success border-success/30"
    : days >= 3
      ? "bg-warning/15 text-warning border-warning/30"
      : "bg-destructive/15 text-destructive border-destructive/30";
  return <Badge variant="outline" className={cls} data-testid="remaining-badge">{days}d</Badge>;
}

function OnlineDot({ online }) {
  return (
    <span
      className={`inline-block h-2.5 w-2.5 rounded-full ${online ? "bg-success" : "bg-muted-foreground/40"}`}
      title={online ? "Online (active in last 5 min)" : "Offline"}
      data-testid={online ? "online-dot" : "offline-dot"}
    />
  );
}

function fmtDuration(secs) {
  if (!secs || secs < 1) return "just now";
  const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60), s = secs % 60;
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m ${s}s`;
  return `${s}s`;
}

function MonitorCell({ customer }) {
  const c = customer;
  const online = c.online;
  const Row = ({ icon: Icon, label, value }) => (
    <div className="flex items-center gap-2 text-xs">
      <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      <span className="text-muted-foreground w-16 shrink-0">{label}</span>
      <span className="font-medium truncate">{value || "—"}</span>
    </div>
  );
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="inline-flex items-center gap-1.5 text-xs hover:opacity-80" data-testid={`monitor-${c.id}`}>
          {online
            ? <><Wifi className="h-3.5 w-3.5 text-success" /><span className="text-success font-medium">Online</span><span className="text-muted-foreground">· {fmtDuration(c.watching_seconds)}</span></>
            : <><WifiOff className="h-3.5 w-3.5 text-muted-foreground/60" /><span className="text-muted-foreground">{c.last_seen ? formatDistanceToNow(new Date(c.last_seen), { addSuffix: true }) : "Never"}</span></>}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 space-y-2" align="start" data-testid={`monitor-pop-${c.id}`}>
        <div className="flex items-center gap-2 pb-1 border-b border-border">
          {online ? <Wifi className="h-4 w-4 text-success" /> : <WifiOff className="h-4 w-4 text-muted-foreground" />}
          <span className={`text-sm font-semibold ${online ? "text-success" : "text-muted-foreground"}`}>{online ? "Online now" : "Offline"}</span>
        </div>
        {online && <Row icon={Clock} label="Watching" value={fmtDuration(c.watching_seconds)} />}
        <Row icon={Tv} label="Channel" value={c.last_channel} />
        <Row icon={MapPin} label="Country" value={c.last_country} />
        <Row icon={Wifi} label="IP" value={c.last_ip} />
        <Row icon={Smartphone} label="App" value={c.last_app} />
        <Row icon={Clock} label={online ? "Since" : "Last seen"} value={c.last_seen ? format(new Date(c.last_seen), "dd-MM-yyyy HH:mm") : "Never"} />
      </PopoverContent>
    </Popover>
  );
}

function randStr(n = 8) {
  return Math.random().toString(36).slice(2, 2 + n);
}

export default function Customers() {
  const { user } = useAuth();
  const [customers, setCustomers] = useState(null);
  const [plans, setPlans] = useState([]);
  const [panels, setPanels] = useState([]);
  // Credits to charge for a plan on a given panel: panel per-plan override (Panels page) else plan default.
  const planCost = (plan, panelId) => {
    if (!plan || plan.is_trial) return 0;
    const panel = panels.find((p) => p.id === panelId);
    const pc = panel && panel.plan_costs ? panel.plan_costs[plan.id] : undefined;
    if (pc !== undefined && pc !== null && pc !== "") return Number(pc);
    return plan.credits;
  };
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [renewFor, setRenewFor] = useState(null);
  const [renewPlan, setRenewPlan] = useState("");
  const [renewing, setRenewing] = useState(false);
  const [connFor, setConnFor] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [resellers, setResellers] = useState([]);
  const [applications, setApplications] = useState([]);
  const [fPanel, setFPanel] = useState("all");
  const [fTemplate, setFTemplate] = useState("all");
  const [fReseller, setFReseller] = useState("all");
  const [exporting, setExporting] = useState("");
  // ActivationPanel (external provider) — additive, isolated from StreamCore creation
  const [apProvider, setApProvider] = useState("streamcore");
  const [apSub, setApSub] = useState("1");
  const [apNote, setApNote] = useState("");
  const [apCreating, setApCreating] = useState(false);
  const [apResult, setApResult] = useState(null);
  const [apProviderName, setApProviderName] = useState("ActivationPanel");
  const [apTemplates, setApTemplates] = useState([]);
  const [apTemplateSel, setApTemplateSel] = useState("");
  const [apManualPack, setApManualPack] = useState("all");
  const [apAccounts, setApAccounts] = useState([]);

  const fetchApAccounts = () =>
    api.get("/external-provider/accounts")
      .then(({ data }) => setApAccounts(data.accounts || []))
      .catch(() => {});

  const [apDiag, setApDiag] = useState(null);
  const [apDiagOpen, setApDiagOpen] = useState(false);
  const [apDiagBusy, setApDiagBusy] = useState("");
  const runApDiag = async (token) => {
    setApDiagBusy(token);
    try {
      const { data } = await api.get(`/external-provider/accounts/${token}/diagnose`);
      setApDiag(data);
      setApDiagOpen(true);
    } catch (e) {
      toast.error("Diagnostic failed");
    } finally {
      setApDiagBusy("");
    }
  };

  const [apFull, setApFull] = useState(null);
  const [apFullOpen, setApFullOpen] = useState(false);
  const [apFullBusy, setApFullBusy] = useState("");
  const runApFull = async (token) => {
    setApFullBusy(token);
    try {
      const { data } = await api.get(`/external-provider/accounts/${token}/diagnose-full`, { timeout: 90000 });
      setApFull(data);
      setApFullOpen(true);
    } catch (e) {
      toast.error("Full diagnostic failed (timeout or error)");
    } finally {
      setApFullBusy("");
    }
  };

  useEffect(() => {
    fetchApAccounts();
  }, [apResult]);

  useEffect(() => {
    api.get("/external-provider/config")
      .then(({ data }) => {
        if (data?.name) setApProviderName(data.name);
        const tpls = data?.templates || [];
        setApTemplates(tpls);
        const def = tpls.find((t) => t.is_default) || tpls[0];
        setApTemplateSel(def ? def.id : "__manual__");
      })
      .catch(() => {});
  }, []);

  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const connInfo = (c) => ({
    long_m3u: `${BACKEND}/get.php?username=${encodeURIComponent(c.username || "")}&password=${encodeURIComponent(c.password || "")}&type=m3u_plus&output=ts`,
    short_m3u: `${BACKEND}/api/s/${encodeURIComponent(c.username || "")}.m3u`,
    server: BACKEND,
    username: c.username || "",
    password: c.password || "",
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const load = async () => {
    try {
      const { data } = await api.get("/customers");
      setCustomers(data);
    } catch { toast.error("Failed to load customers"); }
  };

  useEffect(() => {
    load();
    api.get("/plans").then(({ data }) => setPlans(data)).catch(() => {});
    api.get("/templates").then(({ data }) => setTemplates(data)).catch(() => {});
    api.get("/applications").then(({ data }) => setApplications(data)).catch(() => {});
    if (user.role === "super_admin") {
      api.get("/panels").then(({ data }) => setPanels(data)).catch(() => {});
      api.get("/resellers").then(({ data }) => setResellers(data)).catch(() => {});
    }
  }, [user.role]);

  const appMap = useMemo(() => Object.fromEntries((applications || []).map((a) => [a.id, a])), [applications]);
  const openAppSite = (id) => {
    const a = appMap[id];
    if (!a || !a.website) return toast.error("No website set for this application");
    const href = /^https?:\/\//i.test(a.website) ? a.website : `https://${a.website}`;
    window.open(href, "_blank", "noopener,noreferrer");
  };

  const copyAll = async (c) => {
    const info = connInfo(c);
    const block = [
      `Customer: ${c.full_name}`,
      `Universal M3U (IBO Pro, SmartOne, Hot Player, Smarters): ${info.long_m3u}`,
      `IBO Player (classic) — use this short link: ${info.short_m3u}`,
      `Xtream Server: ${info.server}`,
      `Username: ${info.username}`,
      `Password: ${info.password}`,
      c.expiry_date ? `Expires: ${format(new Date(c.expiry_date), "dd-MM-yyyy HH:mm")}` : "",
    ].filter(Boolean).join("\n");
    await copyToClipboard(block);
    toast.success("All login details copied");
  };

  const doExport = async (fmt) => {
    setExporting(fmt);
    try {
      const { data } = await api.get(`/export/customers?format=${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = `customers.${fmt}`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); } finally { setExporting(""); }
  };

  const filtered = useMemo(() => {
    if (!customers) return [];
    return customers.filter((c) => {
      if (tab === "active") return c.status === "active" && !c.is_trial;
      if (tab === "expired") return c.status === "expired";
      if (tab === "trial") return c.is_trial && c.status === "active";
      return true;
    }).filter((c) => {
      if (fPanel !== "all" && c.panel_id !== fPanel) return false;
      if (fTemplate !== "all" && c.template_id !== fTemplate) return false;
      if (fReseller !== "all" && c.owner_id !== fReseller) return false;
      if (!q) return true;
      const s = q.toLowerCase();
      return [c.full_name, c.username, c.mac_address, c.device_key].some((v) => (v || "").toLowerCase().includes(s));
    });
  }, [customers, tab, q, fPanel, fTemplate, fReseller]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, username: `usr_${randStr(5)}`, password: randStr(10) });
    setApProvider("streamcore"); setApSub("1"); setApNote(""); setApResult(null);
    setApManualPack("all");
    const def = apTemplates.find((t) => t.is_default) || apTemplates[0];
    setApTemplateSel(def ? def.id : "__manual__");
    setOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      full_name: c.full_name || "", username: c.username || "", password: c.password || "",
      mac_address: c.mac_address || "", device_key: c.device_key || "",
      plan_id: c.plan_id || "", panel_id: c.panel_id || "", application_id: c.application_id || "",
      is_trial: c.is_trial, notes: c.notes || "",
      expiry_date: c.expiry_date ? format(new Date(c.expiry_date), "yyyy-MM-dd'T'HH:mm") : "",
    });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, plan_id: form.plan_id || null, panel_id: form.panel_id || null, application_id: form.application_id || null };
      if (payload.expiry_date) {
        payload.expiry_date = new Date(payload.expiry_date).toISOString();
      } else {
        delete payload.expiry_date;
      }
      if (editing) {
        await api.put(`/customers/${editing.id}`, payload);
        toast.success("Customer updated");
      } else {
        await api.post("/customers", payload);
        toast.success("Customer created");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  const doRenew = async () => {
    setRenewing(true);
    try {
      if (renewFor.is_trial) {
        await api.post(`/tests/${renewFor.id}/convert`, { plan_id: renewPlan });
        toast.success("Test converted to paid subscription");
      } else {
        await api.post(`/customers/${renewFor.id}/renew`, { plan_id: renewPlan || renewFor.plan_id });
        toast.success("Subscription renewed");
      }
      setRenewFor(null); setRenewPlan("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setRenewing(false); }
  };

  const remove = async (c) => {
    try { await api.delete(`/customers/${c.id}`); toast.success("Customer deleted"); load(); }
    catch { toast.error("Failed to delete"); }
  };

  const toggleDisable = async (c) => {
    try {
      await api.post(`/customers/${c.id}/disable`, { disabled: !c.disabled });
      toast.success(c.disabled ? "Customer enabled" : "Customer disabled");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const [repairing, setRepairing] = useState(null);
  const doRepair = async (c) => {
    setRepairing(c.id);
    try {
      await api.post(`/customers/${c.id}/repair`);
      toast.success("Link assigned — account repaired");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setRepairing(null); }
  };

  const copy = async (v) => {
    const ok = await copyToClipboard(v);
    ok ? toast.success("Copied") : toast.error("Copy failed — select and copy manually");
  };

  const createOnActivationPanel = async () => {
    const manual = apTemplateSel === "__manual__";
    const tpl = apTemplates.find((t) => t.id === apTemplateSel);
    const pack = manual ? (apManualPack || "all") : (tpl ? tpl.pack_id : "all");
    setApCreating(true);
    setApResult(null);
    try {
      const { data } = await api.post("/external-provider/create-m3u", {
        sub: apSub, pack, note: apNote,
        template_id: manual ? "" : (tpl ? tpl.id : ""),
        template_name: manual ? "" : (tpl ? tpl.name : ""),
      });
      setApResult(data);
      if (data.ok) { toast.success("Account created on ActivationPanel"); fetchApAccounts(); }
      else toast.error(data.error || "Creation failed");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setApCreating(false);
    }
  };

  const counts = useMemo(() => {
    const list = customers || [];
    return {
      all: list.length,
      active: list.filter((c) => c.status === "active" && !c.is_trial).length,
      expired: list.filter((c) => c.status === "expired").length,
      trial: list.filter((c) => c.is_trial && c.status === "active").length,
    };
  }, [customers]);

  return (
    <div data-testid="customers-page">
      <PageHeader overline="Subscribers" title="Customer Management" description="Create, renew and manage IPTV accounts.">
        <div className="flex items-center gap-2">
          <Select value={exporting || ""} onValueChange={doExport}>
            <SelectTrigger className="w-[130px]" data-testid="export-trigger"><Download className="h-4 w-4 mr-1.5" /><SelectValue placeholder="Export" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="csv" data-testid="export-csv">CSV</SelectItem>
              <SelectItem value="xlsx" data-testid="export-xlsx">Excel</SelectItem>
              <SelectItem value="pdf" data-testid="export-pdf">PDF</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={openCreate} data-testid="add-customer-button"><Plus className="h-4 w-4 mr-2" /> New Customer</Button>
        </div>
      </PageHeader>

      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 mb-4">
        <Tabs value={tab} onValueChange={setTab} className="w-full sm:w-auto">
          <TabsList className="h-auto">
            <TabsTrigger value="all" data-testid="tab-all">All <span className="ml-1.5 text-xs text-muted-foreground">{counts.all}</span></TabsTrigger>
            <TabsTrigger value="active" data-testid="tab-active">Active <span className="ml-1.5 text-xs text-muted-foreground">{counts.active}</span></TabsTrigger>
            <TabsTrigger value="expired" data-testid="tab-expired">Expired <span className="ml-1.5 text-xs text-muted-foreground">{counts.expired}</span></TabsTrigger>
            <TabsTrigger value="trial" data-testid="tab-trial">Trial <span className="ml-1.5 text-xs text-muted-foreground">{counts.trial}</span></TabsTrigger>
            <TabsTrigger value="activationpanel" data-testid="tab-activationpanel">{apProviderName} <span className="ml-1.5 text-xs text-muted-foreground">{apAccounts.length}</span></TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative flex-1 min-w-[180px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, username, MAC…" className="pl-9" data-testid="customer-search" />
        </div>
        {user.role === "super_admin" && (
          <Select value={fPanel} onValueChange={setFPanel}>
            <SelectTrigger className="w-[150px]" data-testid="filter-panel"><SelectValue placeholder="Panel" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All panels</SelectItem>{panels.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <Select value={fTemplate} onValueChange={setFTemplate}>
          <SelectTrigger className="w-[150px]" data-testid="filter-template"><SelectValue placeholder="Template" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All templates</SelectItem>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
        </Select>
        {user.role === "super_admin" && (
          <Select value={fReseller} onValueChange={setFReseller}>
            <SelectTrigger className="w-[150px]" data-testid="filter-reseller"><SelectValue placeholder="Reseller" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All resellers</SelectItem>{resellers.map((r) => <SelectItem key={r.id} value={r.id}>{r.name || r.email}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>

      {tab === "activationpanel" && (
        <Card className="border-border overflow-hidden" data-testid="ap-accounts-tab">
          <div className="p-4 border-b border-border text-sm text-muted-foreground">
            {apProviderName} accounts created via StreamCore. Customer M3U URL is masked behind your StreamCore DNS.
          </div>
          {apAccounts.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground">No {apProviderName} accounts created yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-muted-foreground border-b border-border">
                  <tr className="[&>th]:text-left [&>th]:p-3">
                    <th>Username</th><th>Template</th><th>Sub</th><th>Created</th><th>M3U URL</th><th>Diagnose</th>
                  </tr>
                </thead>
                <tbody>
                  {apAccounts.map((a) => (
                    <tr key={a.token} className="border-b border-border/50 [&>td]:p-3" data-testid={`ap-acc-row-${a.token}`}>
                      <td className="font-mono text-xs">{a.username}</td>
                      <td>{a.template_name || <span className="font-mono text-xs text-muted-foreground">#{a.pack}</span>}</td>
                      <td>{a.sub === "99" ? "Demo" : (a.sub ? `${a.sub}m` : "—")}</td>
                      <td className="text-xs text-muted-foreground">{a.created_at ? new Date(a.created_at).toLocaleDateString() : "—"}</td>
                      <td>
                        <div className="flex items-center gap-1">
                          <code className="font-mono text-[11px] break-all max-w-[300px] truncate">{a.streamcore_url}</code>
                          {a.streamcore_url && <Button size="icon" variant="ghost" className="h-6 w-6 shrink-0" onClick={() => copy(a.streamcore_url)} data-testid={`ap-acc-copy-${a.token}`}><Copy className="h-3.5 w-3.5" /></Button>}
                        </div>
                      </td>
                      <td>
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" className="h-7 text-xs" disabled={apDiagBusy === a.token} onClick={() => runApDiag(a.token)} data-testid={`ap-acc-diagnose-${a.token}`}>
                            {apDiagBusy === a.token ? "Running…" : "Diagnose"}
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 text-xs" disabled={apFullBusy === a.token} onClick={() => runApFull(a.token)} data-testid={`ap-acc-full-${a.token}`}>
                            {apFullBusy === a.token ? "Scanning…" : "Full E2E"}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      <Dialog open={apDiagOpen} onOpenChange={setApDiagOpen}>
        <DialogContent className="bg-popover max-w-2xl" data-testid="ap-diag-dialog">
          <DialogHeader>
            <DialogTitle>Provider playlist diagnostic</DialogTitle>
          </DialogHeader>
          {apDiag && (
            <div className="space-y-3 text-sm">
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs">
                <span>Server egress IP: <code className="font-mono">{apDiag.server_egress_ip}</code></span>
                <span>Provider: <code className="font-mono">{apDiag.provider_host_masked}</code></span>
              </div>
              <div className="overflow-x-auto rounded-md border border-border">
                <table className="w-full text-xs">
                  <thead className="text-muted-foreground border-b border-border">
                    <tr className="[&>th]:text-left [&>th]:p-2">
                      <th>User-Agent</th><th>Status</th><th>Content-Type</th><th>Bytes</th><th>#EXTINF</th><th>Valid M3U?</th>
                    </tr>
                  </thead>
                  <tbody>
                    {apDiag.by_user_agent && Object.entries(apDiag.by_user_agent).map(([k, v]) => (
                      <tr key={k} className="border-b border-border/50 [&>td]:p-2" data-testid={`ap-diag-row-${k}`}>
                        <td className="font-mono">{k}</td>
                        <td className={v.http_status === 200 ? "text-emerald-500" : "text-red-500"}>{v.http_status ?? v.error ?? "—"}</td>
                        <td className="font-mono">{v.content_type || "—"}</td>
                        <td>{v.bytes_sampled ?? "—"}</td>
                        <td>{v.extinf_count_in_sample ?? "—"}</td>
                        <td className={v.is_m3u ? "text-emerald-500 font-medium" : "text-red-500"}>{v.is_m3u ? "yes" : "no"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-muted-foreground">{apDiag.note}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={apFullOpen} onOpenChange={setApFullOpen}>
        <DialogContent className="bg-popover max-w-3xl" data-testid="ap-full-dialog">
          <DialogHeader>
            <DialogTitle>End-to-end playlist diagnostic</DialogTitle>
          </DialogHeader>
          {apFull && apFull.ok && (
            <div className="space-y-3 text-sm max-h-[70vh] overflow-y-auto">
              <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 space-y-1">
                <p className="text-xs font-medium text-amber-500">Findings</p>
                {(apFull.findings || []).map((f, i) => (
                  <p key={i} className="text-xs" data-testid={`ap-full-finding-${i}`}>• {f}</p>
                ))}
              </div>
              <div className="overflow-x-auto rounded-md border border-border">
                <table className="w-full text-xs">
                  <thead className="text-muted-foreground border-b border-border">
                    <tr className="[&>th]:text-left [&>th]:p-2"><th>Metric</th><th>Provider M3U</th><th>StreamCore M3U</th></tr>
                  </thead>
                  <tbody className="[&>tr>td]:p-2 [&>tr]:border-b [&>tr]:border-border/50 font-mono">
                    {[
                      ["HTTP status", "http_status"],
                      ["Content-Type", "content_type"],
                      ["Content-Length", "content_length_header"],
                      ["Transfer-Encoding", "transfer_encoding"],
                      ["Total bytes (scanned)", "total_bytes"],
                      ["#EXTINF", "extinf"],
                      ["URL lines", "url_lines"],
                      ["Avg URL length", "url_len_avg"],
                      ["Max URL length", "url_len_max"],
                      ["Avg #EXTINF length", "extinf_len_avg"],
                      ["Rewritten → ep-stream", "rewritten"],
                      ["Provider host leaked", "leaked_provider_host"],
                      ["Malformed lines", "malformed_lines"],
                      ["Ends with newline", "ends_with_newline"],
                      ["Capped (hit limit)", "capped"],
                      ["Elapsed (s)", "elapsed_s"],
                    ].map(([label, key]) => (
                      <tr key={key} data-testid={`ap-full-${key}`}>
                        <td className="text-muted-foreground">{label}</td>
                        <td>{String(apFull.provider?.[key] ?? "—")}</td>
                        <td>{String(apFull.streamcore?.[key] ?? "—")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="rounded-md border border-border p-2">
                <p className="text-xs font-medium mb-1">First rewritten channel probes (first vs subsequent)</p>
                {(apFull.ep_stream_probes || []).map((p, i) => (
                  <p key={i} className="text-xs font-mono" data-testid={`ap-full-probe-${i}`}>
                    ch{i + 1}: status={String(p.status ?? p.error ?? "—")} {p.is_redirect ? `→ redirect (${p.location_host})` : `ct=${p.content_type || "—"}`}
                  </p>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground">Provider host: {apFull.provider_host_masked} · Scan cap: {apFull.caps?.seconds}s / {Math.round((apFull.caps?.bytes || 0) / 1048576)}MB per side. No credentials are read or shown.</p>
            </div>
          )}
          {apFull && !apFull.ok && <p className="text-sm text-red-500">{apFull.error}</p>}
        </DialogContent>
      </Dialog>

      <Card className={`border-border overflow-hidden ${tab === "activationpanel" ? "hidden" : ""}`}>
        {!customers ? (
          <div className="p-4 space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <Users className="h-10 w-10 mx-auto text-muted-foreground" />
            <p className="mt-3 text-sm text-muted-foreground">No customers found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Customer</TableHead>
                  <TableHead>Credentials</TableHead>
                  <TableHead className="hidden lg:table-cell">App</TableHead>
                  <TableHead className="hidden md:table-cell">MAC / Device</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead className="hidden md:table-cell">Panel</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead>Remaining</TableHead>
                  <TableHead className="hidden lg:table-cell">Activity</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((c) => (
                  <TableRow key={c.id} data-testid={`customer-row-${c.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <OnlineDot online={c.online} />
                        <span className="font-medium">{c.full_name}</span>
                        {c.is_trial && <FlaskConical className="h-3.5 w-3.5 text-warning" title="Trial" />}
                        {!c.secure_token && (
                          <Badge variant="outline" className="gap-1 border-warning/40 text-warning text-[10px]" title="This account has no pool link assigned — it will not play. Click Repair." data-testid={`no-link-${c.id}`}>
                            <AlertTriangle className="h-3 w-3" /> No link
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="font-mono text-xs">
                        <div className="flex items-center gap-1"><span className="text-muted-foreground">u:</span>{c.username}
                          <button onClick={() => copy(c.username)} className="opacity-50 hover:opacity-100"><Copy className="h-3 w-3" /></button></div>
                        <div className="flex items-center gap-1"><span className="text-muted-foreground">p:</span>{c.password}
                          <button onClick={() => copy(c.password)} className="opacity-50 hover:opacity-100"><Copy className="h-3 w-3" /></button></div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      {c.application_id && appMap[c.application_id] ? (
                        <div className="flex items-center gap-1.5">
                          <Badge variant="outline" className="gap-1"><AppWindow className="h-3 w-3" />{appMap[c.application_id].name}</Badge>
                          <button onClick={() => openAppSite(c.application_id)} className="opacity-60 hover:opacity-100" title="Open app website" data-testid={`open-app-${c.id}`}><ExternalLink className="h-3.5 w-3.5" /></button>
                        </div>
                      ) : <span className="text-xs text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <div className="font-mono text-xs text-muted-foreground">
                        <div>{c.mac_address || "—"}</div>
                        <div>{c.device_key || "—"}</div>
                      </div>
                    </TableCell>
                    <TableCell><Badge variant="secondary">{c.plan_name || "—"}</Badge></TableCell>
                    <TableCell className="hidden md:table-cell text-xs">{c.panel_name ? <Badge variant="outline" className="gap-1"><Server className="h-3 w-3" />{c.panel_name}</Badge> : <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell className="text-xs text-muted-foreground font-mono">
                      {c.expiry_date ? format(new Date(c.expiry_date), "dd-MM-yyyy HH:mm") : "—"}
                    </TableCell>
                    <TableCell><RemainingDays customer={c} /></TableCell>
                    <TableCell className="hidden lg:table-cell" data-testid={`lastseen-${c.id}`}>
                      <MonitorCell customer={c} />
                    </TableCell>
                    <TableCell><StatusPill status={c.status} /></TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        {c.secure_token ? (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-accent" onClick={() => setConnFor(c)} data-testid={`conn-${c.id}`} title="Connection info (M3U / Xtream)"><Share2 className="h-4 w-4" /></Button>
                        ) : (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-warning hover:text-warning" onClick={() => doRepair(c)} disabled={repairing === c.id} data-testid={`repair-${c.id}`} title="Assign a pool link (repair)">
                            {repairing === c.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wrench className="h-4 w-4" />}
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-accent" onClick={() => { setRenewFor(c); setRenewPlan(c.plan_id || ""); }} data-testid={`renew-${c.id}`} title="Renew"><RefreshCw className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" className={`h-8 w-8 ${c.disabled ? "text-success hover:text-success" : "text-warning hover:text-warning"}`} onClick={() => toggleDisable(c)} data-testid={`disable-${c.id}`} title={c.disabled ? "Enable account" : "Disable account"}>
                          {c.disabled ? <CheckCircle2 className="h-4 w-4" /> : <Ban className="h-4 w-4" />}
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(c)} data-testid={`edit-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" data-testid={`delete-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-popover">
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete {c.full_name}?</AlertDialogTitle>
                              <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => remove(c)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-popover sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Customer" : "New Customer"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            {!editing && (
              <div className="space-y-2">
                <Label>Provider</Label>
                <Select value={apProvider} onValueChange={(v) => { setApProvider(v); setApResult(null); }}>
                  <SelectTrigger data-testid="cust-provider"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover">
                    <SelectItem value="streamcore">StreamCore (default)</SelectItem>
                    <SelectItem value="activationpanel">{apProviderName}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {(editing || apProvider === "streamcore") ? (
            <>
            <div className="space-y-2">
              <Label>Full name</Label>
              <Input value={form.full_name} onChange={set("full_name")} placeholder="John Doe" data-testid="cust-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Username</Label><Input value={form.username} onChange={set("username")} className="font-mono text-sm" data-testid="cust-username" /></div>
              <div className="space-y-2"><Label>Password</Label><Input value={form.password} onChange={set("password")} className="font-mono text-sm" data-testid="cust-password" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>MAC address</Label><Input value={form.mac_address} onChange={set("mac_address")} placeholder="00:1A:79:xx:xx:xx" className="font-mono text-sm" data-testid="cust-mac" /></div>
              <div className="space-y-2"><Label>Device key</Label><Input value={form.device_key} onChange={set("device_key")} className="font-mono text-sm" data-testid="cust-device" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Plan</Label>
                <Select value={form.plan_id} onValueChange={(v) => setForm((f) => ({ ...f, plan_id: v }))}>
                  <SelectTrigger data-testid="cust-plan"><SelectValue placeholder="Select plan" /></SelectTrigger>
                  <SelectContent className="bg-popover">
                    {plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.is_trial ? "Free" : `${planCost(p, form.panel_id)} cr`}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {user.role === "super_admin" && (
                <div className="space-y-2">
                  <Label>Panel</Label>
                  <Select value={form.panel_id} onValueChange={(v) => setForm((f) => ({ ...f, panel_id: v }))}>
                    <SelectTrigger data-testid="cust-panel"><SelectValue placeholder="Auto (best)" /></SelectTrigger>
                    <SelectContent className="bg-popover">
                      {panels.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Package <span className="text-muted-foreground font-normal">(template — channel selection)</span></Label>
              <Select value={form.template_id} onValueChange={(v) => setForm((f) => ({ ...f, template_id: v }))}>
                <SelectTrigger data-testid="cust-template"><SelectValue placeholder="Auto (use link's package)" /></SelectTrigger>
                <SelectContent className="bg-popover">
                  {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Application <span className="text-muted-foreground font-normal">(assigned player)</span></Label>
              <Select value={form.application_id} onValueChange={(v) => setForm((f) => ({ ...f, application_id: v }))}>
                <SelectTrigger data-testid="cust-application"><SelectValue placeholder="Select application" /></SelectTrigger>
                <SelectContent className="bg-popover">
                  {applications.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-md border border-border px-3 py-2.5">
              <div className="flex items-center gap-2"><FlaskConical className="h-4 w-4 text-warning" /><Label className="cursor-pointer">Trial account (1 day)</Label></div>
              <Switch checked={form.is_trial} onCheckedChange={(v) => setForm((f) => ({ ...f, is_trial: v }))} data-testid="cust-trial" />
            </div>
            {editing && (
              <div className="space-y-2">
                <Label>Expiry date &amp; time</Label>
                <Input type="datetime-local" value={form.expiry_date}
                  onChange={(e) => setForm((f) => ({ ...f, expiry_date: e.target.value }))}
                  className="font-mono text-sm" data-testid="cust-expiry" />
                <p className="text-xs text-muted-foreground">Set exactly when this subscription ends (date and time).</p>
              </div>
            )}
            <div className="space-y-2"><Label>Notes</Label><Textarea value={form.notes} onChange={set("notes")} rows={2} data-testid="cust-notes" /></div>
            </>
            ) : (
            <div className="space-y-4" data-testid="ap-create-section">
              <div className="rounded-md border border-border bg-secondary/30 px-3 py-2.5 text-xs text-muted-foreground">
                Create an M3U account directly on ActivationPanel. Your reseller API key stays on the server. Nothing is created until you click the button.
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Subscription</Label>
                  <Select value={apSub} onValueChange={setApSub}>
                    <SelectTrigger data-testid="ap-sub"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-popover">
                      <SelectItem value="1">1 Month</SelectItem>
                      <SelectItem value="3">3 Months</SelectItem>
                      <SelectItem value="6">6 Months</SelectItem>
                      <SelectItem value="12">12 Months</SelectItem>
                      <SelectItem value="99">Test / Demo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Template / Package</Label>
                  <Select value={apTemplateSel} onValueChange={setApTemplateSel}>
                    <SelectTrigger data-testid="ap-template"><SelectValue placeholder="Select template" /></SelectTrigger>
                    <SelectContent className="bg-popover">
                      {apTemplates.map((t) => (
                        <SelectItem key={t.id} value={t.id}>{t.name}{t.is_default ? " (default)" : ""}</SelectItem>
                      ))}
                      <SelectItem value="__manual__">Custom / Manual Package ID</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {apTemplateSel === "__manual__" && (
                <div className="space-y-2">
                  <Label>Manual Package ID</Label>
                  <Input value={apManualPack} onChange={(e) => setApManualPack(e.target.value)} placeholder="all" className="font-mono text-sm" data-testid="ap-manual-pack" />
                </div>
              )}
              <div className="space-y-2">
                <Label>Note <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <Input value={apNote} onChange={(e) => setApNote(e.target.value)} data-testid="ap-note" />
              </div>
              <Button onClick={createOnActivationPanel} disabled={apCreating} data-testid="ap-create-btn">
                {apCreating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Create on ActivationPanel
              </Button>
              {apResult && (
                <div className="rounded-md border border-border" data-testid="ap-create-result">
                  <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
                    {apResult.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <XCircle className="h-4 w-4 text-destructive" />}
                    <span className="text-sm font-medium">{apResult.ok ? "Account created" : "Failed"}</span>
                    {apResult.http_status ? <Badge variant="outline" className="ml-auto text-[10px]">HTTP {apResult.http_status}</Badge> : null}
                  </div>
                  {apResult.error && <p className="px-3 py-2 text-sm text-destructive" data-testid="ap-create-error">{apResult.error}</p>}
                  {apResult.ok && apResult.data && (
                    <div className="px-3 py-2 space-y-1.5">
                      {apResult.streamcore_url && (
                        <div className="flex items-center gap-2 text-sm" data-testid="ap-field-streamcore_url">
                          <span className="w-20 shrink-0 text-muted-foreground">M3U URL</span>
                          <code className="font-mono text-xs break-all text-primary">{apResult.streamcore_url}</code>
                          <Button size="icon" variant="ghost" className="h-6 w-6 ml-auto shrink-0" onClick={() => copy(apResult.streamcore_url)}><Copy className="h-3.5 w-3.5" /></Button>
                        </div>
                      )}
                      {["username", "password", "user_id"].map((k) => (apResult.data[k] != null && apResult.data[k] !== "") ? (
                        <div key={k} className="flex items-center gap-2 text-sm" data-testid={`ap-field-${k}`}>
                          <span className="w-20 shrink-0 text-muted-foreground capitalize">{k.replace("_", " ")}</span>
                          <code className="font-mono text-xs break-all">{String(apResult.data[k])}</code>
                          <Button size="icon" variant="ghost" className="h-6 w-6 ml-auto shrink-0" onClick={() => copy(String(apResult.data[k]))}><Copy className="h-3.5 w-3.5" /></Button>
                        </div>
                      ) : null)}
                      {apResult.streamcore_warning && (
                        <p className="text-xs text-amber-500" data-testid="ap-streamcore-warning">{apResult.streamcore_warning}</p>
                      )}
                      {apResult.streamcore_url && (
                        <p className="text-[11px] text-muted-foreground">This is a StreamCore URL — the ActivationPanel host and credentials stay hidden from the customer.</p>
                      )}
                    </div>
                  )}
                  <pre className="max-h-56 overflow-auto px-3 py-2 text-xs font-mono whitespace-pre-wrap break-all border-t border-border">{JSON.stringify(apResult.data ?? apResult.raw ?? {}, null, 2)}</pre>
                </div>
              )}
            </div>
            )}
          </div>
          {(editing || apProvider === "streamcore") && (
          <DialogFooter>
            <Button onClick={save} disabled={saving || !form.full_name || !form.username} data-testid="cust-save">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {editing ? "Save" : "Create"}
            </Button>
          </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Renew dialog */}
      <Dialog open={!!renewFor} onOpenChange={(o) => !o && setRenewFor(null)}>
        <DialogContent className="bg-popover sm:max-w-sm">
          <DialogHeader><DialogTitle className="font-display">{renewFor?.is_trial ? "Convert to paid" : "Renew subscription"}</DialogTitle></DialogHeader>
          {renewFor && (
            <div className="space-y-4 py-2">
              <p className="text-sm text-muted-foreground">{renewFor.is_trial ? "Convert" : "Extend"} <span className="text-foreground font-medium">{renewFor.username}</span>{renewFor.is_trial ? " into a paid subscription (keeps the reserved link)." : "'s subscription."}</p>
              <div className="space-y-2">
                <Label>Plan</Label>
                <Select value={renewPlan} onValueChange={setRenewPlan}>
                  <SelectTrigger data-testid="renew-plan"><SelectValue placeholder="Select plan" /></SelectTrigger>
                  <SelectContent className="bg-popover">
                    {plans.filter((p) => !p.is_trial).map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {planCost(p, renewFor?.panel_id)} cr</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={doRenew} disabled={renewing || !renewPlan} data-testid="renew-confirm">
              {renewing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {renewFor?.is_trial ? "Convert" : "Renew"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Connection info dialog (M3U + Xtream) */}
      <Dialog open={!!connFor} onOpenChange={(o) => !o && setConnFor(null)}>
        <DialogContent className="bg-popover sm:max-w-md" data-testid="conn-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-success" /> Connection info</DialogTitle></DialogHeader>
          {connFor && (
            <div className="space-y-4 py-2">
              <p className="text-sm text-muted-foreground">Share with <span className="text-foreground font-medium">{connFor.full_name || connFor.username}</span>. The original provider URL stays hidden.</p>
              <div className="space-y-2">
                <Label>Universal M3U link <span className="text-muted-foreground font-normal">(IBO Pro Player, SmartOne, Hot Player, Smarters)</span></Label>
                <div className="flex gap-2">
                  <Input readOnly value={connInfo(connFor).long_m3u} className="font-mono text-xs" data-testid="conn-long-m3u" />
                  <Button size="icon" onClick={() => copy(connInfo(connFor).long_m3u)} data-testid="conn-long-m3u-copy"><Copy className="h-4 w-4" /></Button>
                </div>
              </div>
              <div className="space-y-2 rounded-md border border-warning/50 bg-warning/5 p-3">
                <Label className="text-warning flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> IBO Player (classic) — use THIS short link</Label>
                <p className="text-[11px] text-muted-foreground leading-snug">The classic IBO Player only works with this short M3U link. Choose "Add Playlist" (not XC).</p>
                <div className="flex gap-2">
                  <Input readOnly value={connInfo(connFor).short_m3u} className="font-mono text-xs" data-testid="conn-short-m3u" />
                  <Button size="icon" onClick={() => copy(connInfo(connFor).short_m3u)} data-testid="conn-short-m3u-copy"><Copy className="h-4 w-4" /></Button>
                </div>
              </div>
              <div className="space-y-2 rounded-md border border-accent/30 bg-accent/5 p-3">
                <Label className="text-accent">Xtream Login <span className="text-muted-foreground font-normal">(Hot Player & Xtream apps)</span></Label>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2"><span className="text-xs text-muted-foreground w-16 shrink-0">Server</span><Input readOnly value={connInfo(connFor).server} className="font-mono text-xs" data-testid="conn-xt-server" /><Button size="icon" variant="outline" onClick={() => copy(connInfo(connFor).server)}><Copy className="h-4 w-4" /></Button></div>
                  <div className="flex items-center gap-2"><span className="text-xs text-muted-foreground w-16 shrink-0">Username</span><Input readOnly value={connInfo(connFor).username} className="font-mono text-xs" data-testid="conn-xt-user" /><Button size="icon" variant="outline" onClick={() => copy(connInfo(connFor).username)}><Copy className="h-4 w-4" /></Button></div>
                  <div className="flex items-center gap-2"><span className="text-xs text-muted-foreground w-16 shrink-0">Password</span><Input readOnly value={connInfo(connFor).password} className="font-mono text-xs" data-testid="conn-xt-pass" /><Button size="icon" variant="outline" onClick={() => copy(connInfo(connFor).password)}><Copy className="h-4 w-4" /></Button></div>
                </div>
              </div>
              {user.role === "super_admin" && connFor.original_source && (
                <div className="space-y-2 rounded-md border border-destructive/30 bg-destructive/5 p-3">
                  <Label className="text-destructive flex items-center gap-1.5"><Eye className="h-3.5 w-3.5" /> Original provider link <span className="text-muted-foreground font-normal">(admin only — never share)</span></Label>
                  <div className="flex gap-2">
                    <Input readOnly value={connFor.original_source} className="font-mono text-xs" data-testid="conn-original-source" />
                    <Button size="icon" variant="outline" onClick={() => copy(connFor.original_source)}><Copy className="h-4 w-4" /></Button>
                  </div>
                </div>
              )}
              <Button variant="secondary" className="w-full" onClick={() => copyAll(connFor)} data-testid="copy-all-login">
                <ClipboardCopy className="h-4 w-4 mr-2" /> Copy All Login Details
              </Button>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setConnFor(null)}>Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
